/** Participant activity card: the last 5 sessions of NSE participant-wise
 * index-derivatives positioning — FII / DII / Pro / Retail (NSE's
 * "Client"). Each cell shows the category's NET index-futures stance
 * (long - short contracts, green long / red short) with its change vs the
 * previous session; the tooltip carries the options nets. MARKET-WIDE by
 * nature — NSE publishes no per-index split. Captured server-side each
 * evening (with backfill), so this card works after hours too. */

import type { ReactElement } from "react";
import { useQuery } from "@tanstack/react-query";
import { cn } from "@/lib/cn";
import { marketApi } from "@/lib/marketApi";
import { formatSignedOi } from "@/lib/oiFormat";
import type { ParticipantDay, ParticipantSeries } from "@/types/market";
import { deltaTone } from "./driftShared";

// Data changes once per evening — a slow poll keeps a long-lived tab fresh.
const POLL_MS = 10 * 60_000;

const LABELS: Record<ParticipantSeries["client_type"], string> = {
  FII: "FII",
  DII: "DII",
  Pro: "Pro",
  Client: "Retail",
};

export function ParticipantFlowsCard(): ReactElement {
  const flows = useQuery({
    queryKey: ["market", "participant-flows"],
    queryFn: () => marketApi.participantFlows(),
    refetchInterval: POLL_MS,
  });
  const dates = flows.data?.dates ?? [];
  const participants = flows.data?.participants ?? [];
  return (
    <section className="card mb-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <h2>Participant Activity</h2>
        <span
          className="text-xs text-ink-muted"
          title="NSE participant-wise open interest, index futures + options. Market-wide across NSE index derivatives — no per-index split exists."
        >
          FII · DII · Pro · Retail — net index futures (NSE, market-wide)
        </span>
      </div>
      {dates.length === 0 ? (
        <div className="text-xs text-ink-muted">
          No participant data yet — captured from NSE each evening.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-ink-secondary">
                <th className="py-1 pr-3 text-left font-medium">Participant</th>
                {dates.map((d) => (
                  <th key={d} className="px-2 py-1 text-right font-medium">
                    {dateLabel(d)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {participants.map((p) => (
                <ParticipantRow key={p.client_type} series={p} dates={dates} />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function ParticipantRow({
  series,
  dates,
}: Readonly<{ series: ParticipantSeries; dates: string[] }>): ReactElement {
  const byDate = new Map(series.days.map((d) => [d.trade_date, d]));
  return (
    <tr className="border-t border-line">
      <td className="py-1.5 pr-3 font-medium text-ink">{LABELS[series.client_type]}</td>
      {dates.map((d) => {
        const day = byDate.get(d);
        return (
          <td key={d} className="px-2 py-1.5 text-right">
            {day === undefined ? <span className="text-ink-muted">—</span> : <DayCell day={day} />}
          </td>
        );
      })}
    </tr>
  );
}

function DayCell({ day }: Readonly<{ day: ParticipantDay }>): ReactElement {
  return (
    <div
      title={`Index futures net ${formatSignedOi(day.fut_net)} · calls net ${formatSignedOi(day.call_net)} · puts net ${formatSignedOi(day.put_net)}`}
    >
      <span className={cn("font-mono", deltaTone(day.fut_net))}>{formatSignedOi(day.fut_net)}</span>
      {day.fut_net_change !== null && (
        <div className={cn("font-mono text-[10px]", deltaTone(day.fut_net_change))}>
          {day.fut_net_change >= 0 ? "▲" : "▼"} {formatSignedOi(day.fut_net_change)}
        </div>
      )}
    </div>
  );
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "24 Jul" from an ISO date (a pure date — no timezone games needed). */
function dateLabel(iso: string): string {
  const [, month = "", dayOfMonth = ""] = iso.split("-");
  const name = MONTHS[Number(month) - 1] ?? month;
  return `${String(Number(dayOfMonth))} ${name}`;
}
